import { useStore } from 'hooks/useStore';
import { NADWaiverInfo } from 'Interfaces/BDPortalInterfaces';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';

const NADWaiverLogic = (opportunityIterationID: number, closeModal: () => void) => {
  const {
    UserStore: { userModulesRoles },
    BDPortalStore: {
      concepts,
      getNADwaiver,
      NADWaiverLoading,
      NADWaiverNoteLoading,
      saveNADWaiverNote,
      NADWaiverNotes,
    },
  } = useStore();
  const [currentConceptsIndex, setCurrentConceptsIndex] = useState<number>(0);
  const [NADWaiversByStrength, setNADWaiversByStrength] = useState<{
    [key: string]: NADWaiverInfo | null;
  }>({});
  const [isError404, setIsError404] = useState<boolean>(false);
  const [savingStrength, setSavingStrength] = useState<string | null>(null);
  const [originalNADWaivers, setOriginalNADWaivers] = useState<{
    [key: string]: NADWaiverInfo | null;
  }>({});
  const [note, setNote] = useState<string>('');
  const [saveError, setSaveError] = useState<
    { field: string; depends: string; error: string }[] | null
  >(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [_isNADWaiverSaved, setIsNADWaiverSaved] = useState<boolean>(false);

  const errorCheck = useCallback((
    data: NADWaiverInfo
  ): { field: string; depends: string; error: string }[] | null => {
    const save_errors: { field: string; depends: string; error: string }[] = [];
    if (data.Status.toLowerCase() === 'approved' && data.StatusDate === null) {
      save_errors.push({
        field: 'StatusDate',
        depends: 'Status',
        error: 'Approved Date is required.',
      });
    }
    if (data.SubmissionDate === null && data.Submitted) {
      save_errors.push({
        field: 'SubmissionDate',
        depends: 'Submitted',
        error: 'Submission Date is required.',
      });
    }
    if (data.Status.toLowerCase() === 'approved' && data.ExpirationDate === null) {
      save_errors.push({
        field: 'ExpirationDate',
        depends: 'Status',
        error: 'Expiration Date is required.',
      });
    }
    if (save_errors.length === 0) {
      return null;
    } else {
      return save_errors;
    }
  }, []);

  const saveStrength = async (strength: string) => {
    const currentNADWaiver = NADWaiversByStrength[strength];
    if (!currentNADWaiver) return;

    setSavingStrength(strength);

    try {
      const payload: NADWaiverInfo = {
        ...currentNADWaiver,
        Strength: strength,
      };

      const checkForError = errorCheck(payload);

      if (checkForError === null) {
        await bdportalScreenServices.saveNADWaiverDetails(payload);
        toast.success(`Successfully saved NAD Waiver for strength: ${strength}`);
        setSaveError(null);
        setIsNADWaiverSaved(true);
      } else {
        setSaveError([...checkForError]);
      }

      // Update the original NADWaivers to match the current state
      setOriginalNADWaivers(prev => ({
        ...prev,
        [strength]: { ...currentNADWaiver },
      }));
    } catch (err) {
      console.error(`Error saving NAD Waiver for strength: ${strength}`, err);
      setIsNADWaiverSaved(false);
    } finally {
      setSavingStrength(null);
    }
  };

  const handleNoteChange = (content: string) => {
    const normalizedContent = content.trim();
    setNote(normalizedContent === '<p><br></p>' ? '' : normalizedContent);
  };

  const hasUnsavedChangesForStrength = useCallback((strength: string) => {
    const original = originalNADWaivers[strength];
    const current = NADWaiversByStrength[strength];

    if (!original || !current) return false;

    return JSON.stringify(original) !== JSON.stringify(current);
  }, [originalNADWaivers, NADWaiversByStrength]);

  const hasUnsavedChanges = useCallback(() => {
    return Object.keys(NADWaiversByStrength).some(strength =>
      hasUnsavedChangesForStrength(strength)
    );
  }, [NADWaiversByStrength, hasUnsavedChangesForStrength]);

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleCloseNADWaiverModal = useCallback(() => {
    if (JSON.stringify(NADWaiversByStrength) !== JSON.stringify(originalNADWaivers)) {
      setIsConfirmModalOpen(true);
    } else {
      closeModal();
    }
  }, [NADWaiversByStrength, originalNADWaivers, closeModal]);

  const handleCloseConfirmationModal = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  const handleConfirmForConfirmationModal = useCallback(() => {
    setIsConfirmModalOpen(false);
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const handleOpenNADWaiver = async (oppIID: number) => {
      try {
        const response = await getNADwaiver(oppIID, signal);

        if (response === null) {
          const defaultNADWaivers = concepts?.Concept_info.reduce(
            (acc, concept) => {
              acc[concept.Strength] = {
                Submitted: false,
                SubmissionDate: null,
                Status: 'Pending',
                StatusDate: null,
                IsActive: null,
                ExpirationDate: null,
                IsExisting: false,
                Strength: concept.Strength,
                OpportunityIterationID: oppIID,
              };
              return acc;
            },
            {} as { [key: string]: NADWaiverInfo }
          );

          setNADWaiversByStrength(defaultNADWaivers || {});
          setOriginalNADWaivers(defaultNADWaivers || {});
        } else {
          const nadWaiversByStrength = concepts?.Concept_info.reduce(
            (acc, concept) => {
              const strengthKey = concept.Strength;
              acc[strengthKey] = response?.Strength[strengthKey]?.info || {
                Submitted: false,
                SubmissionDate: null,
                Status: 'Pending',
                StatusDate: null,
                IsActive: null,
                ExpirationDate: null,
                IsExisting: false,
                Strength: strengthKey,
                OpportunityIterationID: opportunityIterationID,
              };
              return acc;
            },
            {} as { [key: string]: NADWaiverInfo }
          );

          setNADWaiversByStrength(nadWaiversByStrength || {});
          setOriginalNADWaivers(nadWaiversByStrength || {});
        }
      } catch (err: unknown) {
        const error = err as { status?: number };
        if (error.status === 404) setIsError404(true);
      }
    };

    handleOpenNADWaiver(opportunityIterationID);

    return () => {
      controller.abort();
    };
  }, [opportunityIterationID, concepts, getNADwaiver]);

  return {
    userModulesRoles,
    currentConceptsIndex,
    concepts,
    setCurrentConceptsIndex,
    NADWaiversByStrength,
    setNADWaiversByStrength,
    NADWaiverLoading,
    isError404,
    savingStrength,
    saveStrength,
    hasUnsavedChanges,
    hasUnsavedChangesForStrength,
    handleClose,
    NADWaiverNoteLoading,
    note,
    setNote,
    handleNoteChange,
    saveNADWaiverNote,
    NADWaiverNotes,
    saveError,
    setSaveError,
    isConfirmModalOpen,
    handleCloseNADWaiverModal,
    handleConfirmForConfirmationModal,
    handleCloseConfirmationModal,
  };
};

export default NADWaiverLogic;
